import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SHORT_TOUCH_QUERY } from '../../hooks/useIsPhone';
import { Avatar, Box, Typography, FormControl, Select, MenuItem, InputLabel, IconButton, Collapse, useTheme, useMediaQuery } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';

import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTeamspace } from '../../context/TeamspaceProvider';
import PagePresence from '../Presence/PagePresence';
import ProfilePicturePreview from '../ProfilePicturePreview';
import { profilePictureUrl, memberInitials } from '../../utils/profilePicture';
import { fetchTimeEntriesForAutocomplete } from '../../context/TeamspaceProvider';

// ---------------------------------------------------------------------------
// Weekly Summary
//
// Declared at MODULE scope on purpose. This used to live inside Header's body,
// which meant a brand-new component *type* was created on every Header render.
// React cannot reconcile a new type, so it unmounted and remounted the whole
// subtree each time: state reset to null, the lastFetchKey guard ref reset, the
// block dropped out of the DOM and immediately refetched. Any context update
// (websocket-driven member/teamspace refresh, or a scroll toggling `scrolled`)
// triggered that churn, and the collapse-then-restore is what made the page
// twitch vertically. Do not move this back inside Header.
// ---------------------------------------------------------------------------

// Reserved height for the summary block, so the header row keeps a constant
// height whether the durations are loaded, loading, or not yet fetched.
const WEEKLY_SUMMARY_MIN_HEIGHT = 44;

// Height reserved for the header content row. The teamspace Select (56px) is
// the tallest child, so pinning this keeps the sticky header a fixed size.
const HEADER_ROW_MIN_HEIGHT = 56;

const calculateWeekRange = (date, weeksAgo = 0) => {
  const d = new Date(date);
  // Start of week (Monday). getDay(): Sun=0..Sat=6
  // Convert to "days since Monday": Mon=0..Sun=6
  const daysSinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - daysSinceMonday - (weeksAgo * 7));
  const start = new Date(d);
  const end = new Date(d);
  end.setDate(end.getDate() + 6); // End of week (Sunday)
  end.setHours(23, 59, 59, 999);
  return { start, end };
};

const formatDuration = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const formatWeekRange = (start, end) => {
  const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
  const endDay = end.getDate();

  if (start.getMonth() === end.getMonth()) {
    return `${startMonth} ${startDay} – ${endDay}`;
  } else {
    return `${startMonth} ${startDay} – ${endMonth} ${endDay}`;
  }
};

// Fixed advance width + tabular figures, so '--:--' and '40:15' occupy exactly
// the same box. Without this the line reflows every time the summary refreshes.
const DurationText = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontWeight: 800,
      fontSize: '1.05rem',
      color: 'inherit',
      display: 'inline-block',
      minWidth: '5ch',
      textAlign: 'right',
      fontVariantNumeric: 'tabular-nums',
      fontFeatureSettings: '"tnum"',
    }}
  >
    {children}
  </Box>
);

const DateText = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontWeight: 800,
      color: 'inherit',
      display: 'inline-block',
      minWidth: '9ch',
      fontVariantNumeric: 'tabular-nums',
      fontFeatureSettings: '"tnum"',
    }}
  >
    {children}
  </Box>
);

// `stacked` (mobile top-bar panel only): title and each week on their own lines,
// allowed to wrap. The desktop render below is unchanged.
const WeeklySummary = ({ selectedTeamspaceId, selectedTeamspace, user, members, stacked = false }) => {
  const theme = useTheme();
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [weekRanges, setWeekRanges] = useState(null);
  const lastFetchKeyRef = useRef(null);
  const fetchSeqRef = useRef(0);
  const hasDataRef = useRef(false);

  // Rounding settings come from the selected teamspace (same as TimeEntriesListPage / TrackingSummary)
  const applyRounding = selectedTeamspace?.rounding || false;
  const roundingMinutes = selectedTeamspace?.rounding_minutes ? parseInt(selectedTeamspace.rounding_minutes) : 1;

  // `user` and `members` arrive as fresh object/array references on every
  // context update, so depending on them directly restarts the fetch endlessly.
  // Depend on primitives instead and read the live values through refs.
  const userKey = user?.username || user?.email || '';
  const membersKey = Array.isArray(members) ? members.length : 0;
  const userRef = useRef(user);
  const membersRef = useRef(members);
  userRef.current = user;
  membersRef.current = members;

  useEffect(() => {
    if (!selectedTeamspaceId || !userKey) return;

    const fetchWeeklyData = async () => {
      const currentUser = userRef.current;
      const currentMembers = membersRef.current;

      // Create a stable "fetch key" so we do not re-fetch (and flicker) for the same inputs
      const fetchKey = `${selectedTeamspaceId}|${userKey}|${membersKey}|${applyRounding ? 1 : 0}|${roundingMinutes}`;

      // If we have already fetched for this exact key, do not trigger another load cycle
      if (lastFetchKeyRef.current === fetchKey && hasDataRef.current) {
        return;
      }

      lastFetchKeyRef.current = fetchKey;
      const seq = ++fetchSeqRef.current;
      setLoading(true);
      try {
        // Round UP to the nearest rounding increment (ceil), matching the rest of the app
        const roundDuration = (durationInSeconds, roundingMins) => {
          if (!applyRounding) return durationInSeconds;
          const roundingSeconds = roundingMins * 60;
          return Math.ceil(durationInSeconds / roundingSeconds) * roundingSeconds;
        };

        // Identify the current user's TeamspaceMember record for the selected teamspace
        const currentUserMember = Array.isArray(currentMembers)
          ? currentMembers.find(m => (
              (currentUser?.email && m.email === currentUser.email) ||
              (currentUser?.username && m.username === currentUser.username) ||
              (currentUser?.email && m.label === currentUser.email) ||
              (currentUser?.username && m.label === currentUser.username)
            ))
          : null;

        // Prefer username for backend filter param (CalendarPage uses member_username)
        // Fallback to email/label if username doesn't exist.
        const memberUsernameParam =
          currentUserMember?.username ||
          currentUser?.username ||
          currentUserMember?.email ||
          currentUser?.email ||
          currentUserMember?.label ||
          null;

        const now = new Date();
        const currentWeek = calculateWeekRange(now, 0);
        const previousWeek = calculateWeekRange(now, 1);

        // Compute display ranges immediately so we can render while durations load
        setWeekRanges({
          current: formatWeekRange(currentWeek.start, currentWeek.end),
          previous: formatWeekRange(previousWeek.start, previousWeek.end),
        });

        // Fetch data for both weeks
        const [currentWeekData, previousWeekData] = await Promise.all([
          fetchTimeEntriesForAutocomplete(selectedTeamspaceId, currentWeek.start, currentWeek.end, memberUsernameParam),
          fetchTimeEntriesForAutocomplete(selectedTeamspaceId, previousWeek.start, previousWeek.end, memberUsernameParam)
        ]);

        // Client-side safety filter in case backend ignores member_username param
        const identifiers = [
          currentUser?.username,
          currentUser?.email,
          currentUserMember?.username,
          currentUserMember?.email,
          currentUserMember?.label
        ]
          .filter(Boolean)
          .map(v => String(v).toLowerCase());

        const isCurrentUserEntry = (entry) => {
          if (!entry) return false;

          // Prefer matching by member id if available
          if (currentUserMember?.id != null && entry.member != null) {
            if (String(entry.member) === String(currentUserMember.id)) return true;
          }

          // Fallback: match by member_username or email-ish fields
          const entryMember =
            entry.member_username ||
            entry.memberUsername ||
            entry.member_email ||
            entry.memberEmail ||
            null;

          if (!entryMember) return false;
          return identifiers.includes(String(entryMember).toLowerCase());
        };

        const currentWeekEntries = Array.isArray(currentWeekData) ? currentWeekData.filter(isCurrentUserEntry) : [];
        const previousWeekEntries = Array.isArray(previousWeekData) ? previousWeekData.filter(isCurrentUserEntry) : [];

        const calculateWeekStats = (entries) => {
          let totalDuration = 0;
          let billableDuration = 0;

          entries.forEach(entry => {
            const originalDuration = entry.duration || 0;
            const duration = roundDuration(originalDuration, roundingMinutes);
            const isBillable = entry.billable === true;

            totalDuration += duration;
            if (isBillable) {
              billableDuration += duration;
            }
          });

          const nonBillableDuration = totalDuration - billableDuration;

          return {
            total: totalDuration,
            billable: billableDuration,
            nonBillable: nonBillableDuration,
            totalFormatted: formatDuration(totalDuration),
            billableFormatted: formatDuration(billableDuration),
            nonBillableFormatted: formatDuration(nonBillableDuration)
          };
        };

        const currentStats = calculateWeekStats(currentWeekEntries);
        const previousStats = calculateWeekStats(previousWeekEntries);

        // Ignore stale results if a newer fetch started
        if (seq !== fetchSeqRef.current) return;

        hasDataRef.current = true;
        setWeeklyData({
          currentWeek: {
            ...currentStats,
            range: formatWeekRange(currentWeek.start, currentWeek.end),
            year: currentWeek.end.getFullYear()
          },
          previousWeek: {
            ...previousStats,
            range: formatWeekRange(previousWeek.start, previousWeek.end),
            year: previousWeek.end.getFullYear()
          }
        });
      } catch (error) {
        console.error('Error fetching weekly summary:', error);
        // Clear the key so the next dependency change can retry, instead of
        // sticking on a key that never produced data.
        lastFetchKeyRef.current = null;
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
        }
      }
    };

    fetchWeeklyData();
  }, [selectedTeamspaceId, userKey, membersKey, applyRounding, roundingMinutes]);

  const currentRange = weekRanges?.current || weeklyData?.currentWeek?.range || '';
  const previousRange = weekRanges?.previous || weeklyData?.previousWeek?.range || '';
  const hasRanges = Boolean(currentRange || previousRange);

  // While loading, show a placeholder for durations only (keep the title visible)
  const displayCurrentTotal = loading ? '--:--' : (weeklyData?.currentWeek?.totalFormatted || '00:00');
  const displayCurrentBillable = loading ? '--:--' : (weeklyData?.currentWeek?.billableFormatted || '00:00');
  const displayPreviousTotal = loading ? '--:--' : (weeklyData?.previousWeek?.totalFormatted || '00:00');
  const displayPreviousBillable = loading ? '--:--' : (weeklyData?.previousWeek?.billableFormatted || '00:00');

  if (stacked) {
    const lineSx = { color: theme.palette.text.secondary, lineHeight: 1.4, fontSize: '0.875rem', visibility: hasRanges ? 'visible' : 'hidden' };
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.primary, fontSize: '0.875rem', lineHeight: 1.3 }}>
          Your Weekly Summary ({selectedTeamspace?.name || 'Teamspace'})
        </Typography>
        <Typography variant="caption" sx={lineSx}>
          <DateText>{currentRange}</DateText> <DurationText>{displayCurrentTotal}</DurationText> total (<DurationText>{displayCurrentBillable}</DurationText> billable)
        </Typography>
        <Typography variant="caption" sx={lineSx}>
          <DateText>{previousRange}</DateText> <DurationText>{displayPreviousTotal}</DurationText> total (<DurationText>{displayPreviousBillable}</DurationText> billable)
        </Typography>
      </Box>
    );
  }

  // This block always renders and always occupies WEEKLY_SUMMARY_MIN_HEIGHT.
  // Returning null (or letting a line wrap) resizes the sticky header, which
  // shoves the whole page up and down. Both lines are nowrap + clipped so the
  // line count stays invariant however long the teamspace name or ranges get.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: 0.5,
        ml: 3,
        minWidth: 0,
        minHeight: `${WEEKLY_SUMMARY_MIN_HEIGHT}px`,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: theme.palette.text.primary,
          fontSize: '0.95rem',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Your Weekly Summary ({selectedTeamspace?.name || 'Teamspace'})
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          fontSize: '0.95rem',
          overflow: 'hidden',
          // Keep the line's box (and so the header height) even before the
          // first fetch resolves.
          visibility: hasRanges ? 'visible' : 'hidden',
        }}
      >
        <DateText>{currentRange}</DateText>   <DurationText>{displayCurrentTotal}</DurationText> total (<DurationText>{displayCurrentBillable}</DurationText> billable)
        <Box component="span" sx={{ mx: 3 }} />
        <DateText>{previousRange}</DateText>   <DurationText>{displayPreviousTotal}</DurationText> total (<DurationText>{displayPreviousBillable}</DurationText> billable)
      </Typography>
    </Box>
  );
};

// `zoom` (optional) scales the whole header — App.js passes PAGE_ZOOM on every
// desktop route (and on the zoomed pages on mobile) so it is one size app-wide. It sits on the sticky root itself: a
// zoomed wrapper around it would be exactly its height and pin it in place.
// `onMenuClick` opens the sidebar drawer; only the mobile top bar uses it.
const Header = ({ zoom, onMenuClick }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { teamspaces, selectedTeamspaceId, setSelectedTeamspaceId, displayTimezone, timezoneReady, userPreferences, members } = useTeamspace();
  const theme = useTheme();
  // Same switch as App.js, sideways phones included.
  const isMobile = useMediaQuery(`${theme.breakpoints.down('md')}, ${SHORT_TOUCH_QUERY}`);
  // Mobile top bar: the weekly-summary panel under the bar, closed by default.
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Your own picture, enlarged from the header avatar: { url, alt } or null.
  const [ownPicturePreview, setOwnPicturePreview] = useState(null);
  const ownPictureUrl = profilePictureUrl(user?.email, user?.profile_picture_version);
  const [scrolled, setScrolled] = useState(false);
  
  // Track previous timezone value to detect changes
  const prevTimezoneRef = useRef(null);
  
  // Log when timezone is set or changed in header
  // Only log when timezone is actually ready (not null) to avoid logging UTC prematurely
  useEffect(() => {
    if (timezoneReady && displayTimezone) {
      // First time setting
      if (prevTimezoneRef.current === null) {
        console.log('🌍 [Header] Timezone initialized:', displayTimezone, {
          use_device_timezone: userPreferences?.use_device_timezone,
          selectedTeamspaceId,
          timezoneReady
        });
        prevTimezoneRef.current = displayTimezone;
      } 
      // Timezone changed
      else if (prevTimezoneRef.current !== displayTimezone) {
        console.log('🌍 [Header] Timezone changed:', {
          from: prevTimezoneRef.current,
          to: displayTimezone,
          use_device_timezone: userPreferences?.use_device_timezone,
          selectedTeamspaceId,
          timezoneReady
        });
        prevTimezoneRef.current = displayTimezone;
      }
    }
  }, [displayTimezone, timezoneReady, userPreferences?.use_device_timezone, selectedTeamspaceId]);
  
  // Format timezone for display
  const formatTimezone = (tz) => {
    // Convert timezone to a more readable format
    // e.g., "Asia/Manila" -> "Asia/Manila (UTC+8)"
    const timezoneMap = {
      'UTC': 'UTC',
      'Asia/Manila': 'Asia/Manila (UTC+8)',
      'Asia/Tokyo': 'Asia/Tokyo (UTC+9)',
      'Asia/Bangkok': 'Asia/Bangkok (UTC+7)',
      'Asia/Kolkata': 'Asia/Kolkata (UTC+5:30)',
    };
    
    // If it's in the map, use the formatted version
    if (timezoneMap[tz]) {
      return timezoneMap[tz];
    }
    
    // For device timezone or other timezones, try to format it nicely
    // Extract city name from timezone string (e.g., "America/New_York" -> "New York")
    const parts = tz.split('/');
    if (parts.length > 1) {
      const city = parts[parts.length - 1].replace(/_/g, ' ');
      // Try to get UTC offset
      try {
        const now = new Date();
        const formatter = new Intl.DateTimeFormat('en', {
          timeZone: tz,
          timeZoneName: 'shortOffset'
        });
        const parts = formatter.formatToParts(now);
        const offsetPart = parts.find(part => part.type === 'timeZoneName');
        if (offsetPart) {
          return `${city} (${offsetPart.value})`;
        }
      } catch (e) {
        // If formatting fails, just return the city name
      }
      return city;
    }
    
    return tz;
  };

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleTeamspaceChange = (newTeamspaceId) => {
    setSelectedTeamspaceId(newTeamspaceId);
    
    // Extract the current route path (everything after /:teamspaceId/)
    const pathMatch = location.pathname.match(/^\/(\d+)\/(.+)$/);
    if (pathMatch) {
      // Current path has a teamspace ID, preserve the route
      const [, , currentRoute] = pathMatch;
      navigate(`/${newTeamspaceId}/${currentRoute}`);
    } else {
      // No teamspace ID in current path, default to time_entries
      navigate(`/${newTeamspaceId}/time_entries`);
    }
  };

  const selectedTeamspace = teamspaces.find(ts => ts.id === selectedTeamspaceId);
  const teamspaceDisplayName = selectedTeamspace ? selectedTeamspace.name : "Teamspaces";


  // Mobile (below md): a slim top bar — menu, teamspace, summary toggle, avatar —
  // with the weekly summary in a panel beneath it. It replaced a floating centre
  // toggle that sat on top of page content. The desktop render is below.
  if (isMobile) {
    return (
      <Box sx={{ position: 'sticky', top: 0, zIndex: theme.zIndex.appBar + 1, zoom }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1,
          height: HEADER_ROW_MIN_HEIGHT,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}>
          <IconButton aria-label="Open menu" onClick={onMenuClick} color="inherit">
            <MenuIcon />
          </IconButton>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0, maxWidth: 240 }}>
            <Select
              id="teamspace-select-mobile"
              value={selectedTeamspaceId || ''}
              onChange={(e) => handleTeamspaceChange(e.target.value)}
              inputProps={{ 'aria-label': 'Teamspace' }}
              sx={{
                color: theme.palette.text.primary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.info.main + '40' },
                '& .MuiSelect-icon': { color: theme.palette.text.primary },
              }}
            >
              {teamspaces.map((teamspace) => (
                <MenuItem key={teamspace.id} value={teamspace.id}>
                  {teamspace.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <IconButton
            aria-label={summaryOpen ? 'Hide weekly summary' : 'Show weekly summary'}
            aria-expanded={summaryOpen}
            onClick={() => setSummaryOpen((open) => !open)}
            color="inherit"
          >
            {summaryOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Avatar
            src={ownPictureUrl || undefined}
            alt={user?.username || ''}
            onClick={() => navigate(`/${selectedTeamspaceId}/profile`)}
            aria-label="Open profile"
            role="button"
            sx={{ width: 32, height: 32, fontSize: 14, cursor: 'pointer', mr: 0.5 }}
          >
            {memberInitials(user?.first_name, user?.last_name) || undefined}
          </Avatar>
        </Box>
        {/* Stays mounted while closed so the summary fetches once, not per open. */}
        <Collapse in={summaryOpen} timeout="auto">
          <Box sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
          }}>
            <WeeklySummary
              selectedTeamspaceId={selectedTeamspaceId}
              selectedTeamspace={selectedTeamspace}
              user={user}
              members={members}
              stacked
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <PagePresence />
              <Typography variant="caption" sx={{ fontSize: '0.75rem', opacity: 0.8, textAlign: 'right', overflowWrap: 'anywhere' }}>
                {user?.username || user?.email_alt || 'User'}
                {' · '}
                {displayTimezone ? formatTimezone(displayTimezone) : 'Loading...'}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'sticky', top: 0, zIndex: theme.zIndex.appBar + 1, zoom }}>
      {/* Desktop only (mobile returned above), so always open. */}
      <Collapse in timeout="auto" unmountOnExit>
        <Box sx={{
          px: 2,
          py: 1.5,
          // Pinned: the teamspace Select (56px) is the tallest child. Without
          // this, anything that resizes inside the header moves the whole page.
          minHeight: HEADER_ROW_MIN_HEIGHT,
          borderBottom: scrolled ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: scrolled ? theme.palette.background.default : 'transparent',
          backdropFilter: scrolled ? 'saturate(120%) blur(6px)' : 'none',
          boxShadow: scrolled ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
          transition: 'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease, backdrop-filter 120ms ease',
          color: theme.palette.text.primary
        }}>
          {/* Left: Teamspace Selector */}
          {/* No overflow:hidden here — the Select's shrunk "Teamspace" label
              floats ~9px above the input box and would be clipped. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="teamspace-select-label" sx={{ color: theme.palette.text.primary }}>
                Teamspace
              </InputLabel>
              <Select
                labelId="teamspace-select-label"
                id="teamspace-select"
                value={selectedTeamspaceId || ''}
                label="Teamspace"
                onChange={(e) => handleTeamspaceChange(e.target.value)}
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '40',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main + '80',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.info.main,
                  },
                  '& .MuiSelect-icon': {
                    color: theme.palette.text.primary,
                  }
                }}
              >
                {teamspaces.map((teamspace) => (
                  <MenuItem key={teamspace.id} value={teamspace.id}>
                    {teamspace.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <WeeklySummary
              selectedTeamspaceId={selectedTeamspaceId}
              selectedTeamspace={selectedTeamspace}
              user={user}
              members={members}
            />
          </Box>

          {/* Right: who else is on this page, overlapping in behind your own
              avatar and stacking leftwards, then you */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PagePresence />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Avatar: enlarges your picture (nothing to enlarge without one). */}
            <Avatar
              src={ownPictureUrl || undefined}
              alt={user?.username || ''}
              onClick={ownPictureUrl
                ? () => setOwnPicturePreview({ url: ownPictureUrl, alt: user?.username || '' })
                : undefined}
              aria-label={ownPictureUrl ? 'View your profile picture' : undefined}
              sx={{
                width: 32,
                height: 32,
                fontSize: 14,
                cursor: ownPictureUrl ? 'pointer' : 'default',
                // Anchor of the presence stack: drawn above the avatars tucked behind it.
                position: 'relative',
                zIndex: 10,
                border: `2px solid ${theme.palette.background.default}`,
              }}
            >
              {memberInitials(user?.first_name, user?.last_name) || undefined}
            </Avatar>
            {/* Username + timezone: the only part that opens your profile page. */}
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', cursor: 'pointer' }}
              onClick={() => navigate(`/${selectedTeamspaceId}/profile`)}
              aria-label="Open profile"
              role="button"
            >
              <Typography variant="subtitle1">
                {user?.username || user?.email_alt || 'User'}
              </Typography>
                      {/* Always show timezone - displayTimezone now always returns a value */}
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          fontSize: '0.7rem', 
                          opacity: displayTimezone ? 0.8 : 0.5,
                          lineHeight: 1,
                          fontStyle: timezoneReady ? 'normal' : 'italic'
                        }}
                      >
                        {displayTimezone ? formatTimezone(displayTimezone) : 'Loading...'}
                      </Typography>
            </Box>
          </Box>
          </Box>
        </Box>
      </Collapse>
      <ProfilePicturePreview picture={ownPicturePreview} onClose={() => setOwnPicturePreview(null)} />
    </Box>
  );
};

export default Header;